import { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, Typography,
} from '@mui/material';
import {
  defaultSelectedIds,
  keepReason,
  tickReason,
  type RezoneDefaultContext,
  type RezoneMove,
  type RezonePlan,
} from '../../../lib/rezoneItinerary';

const eventWord = (n: number) => (n === 1 ? 'event' : 'events');

/**
 * The opt-in that comes BEFORE a new trip zone is saved (or on its own, from the
 * "still on another zone" notice).
 *
 * Every itinerary item stores its own zone and that wins on the guest site, so a
 * new trip zone alone leaves events that were saved under the host's browser zone
 * reading "8:00 PM · America/Chicago" on a Lisbon trip. This offers to move them,
 * keeping their local times, one checkbox per event.
 *
 * Only an event that was almost certainly filed under the fallback by mistake
 * starts ticked (see defaultSelectedIds); an event in another zone, or any event
 * on a trip that already had a zone, starts unticked with the reason beside it.
 *
 * Nothing here sends anything. The caller offers a change notice AFTER the moves
 * land, for the moved events guests can see.
 */
export default function RezoneDialog({
  plan,
  savesZone,
  defaults,
  busy,
  onCancel,
  onConfirm,
}: {
  plan: RezonePlan;
  /** True when confirming also saves the trip zone; false for a re-zone alone. */
  savesZone: boolean;
  /** What decides the pre-ticked rows: whether the trip had a zone, and the host's own zone. */
  defaults: RezoneDefaultContext;
  busy: boolean;
  onCancel: () => void;
  /** The moves the host ticked; empty means "save the zone only". */
  onConfirm: (selected: RezoneMove[]) => void;
}) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(defaultSelectedIds(plan.moves, defaults)),
  );
  const cancelled = plan.skipped.filter((s) => s.reason === 'cancelled');
  const unconvertible = plan.skipped.filter((s) => s.reason !== 'cancelled');
  const chosen = plan.moves.filter((move) => selected.has(move.item.id));
  const count = chosen.length;

  const toggle = (id: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    setSelected(next);
  };

  const confirmLabel = busy
    ? 'Saving…'
    : savesZone
      ? (count > 0 ? `Save and move ${count} ${eventWord(count)}` : 'Save time zone')
      : (count > 0 ? `Move ${count} ${eventWord(count)}` : 'Nothing selected');

  return (
    <Dialog open onClose={busy ? undefined : onCancel} fullWidth maxWidth="sm">
      <DialogTitle>{savesZone ? 'Save time zone' : `Move events to ${plan.toZone}`}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          {plan.moves.length} {eventWord(plan.moves.length)} {plan.moves.length === 1 ? 'is' : 'are'} saved
          in a different zone than {plan.toZone}. Tick the ones to move; a moved event keeps its local
          time and changes only the zone it is read in. Unticked events stay exactly as they are.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
          <Button
            size="small"
            disabled={busy}
            onClick={() => setSelected(new Set(plan.moves.map((move) => move.item.id)))}
          >
            Select all
          </Button>
          <Button size="small" disabled={busy} onClick={() => setSelected(new Set())}>
            Select none
          </Button>
        </Stack>

        <Stack component="ul" spacing={0.5} sx={{ pl: 0, my: 1, listStyle: 'none' }}>
          {plan.moves.map((move) => {
            const reason = keepReason(move, defaults) ?? tickReason(move);
            return (
              <Box component="li" key={move.item.id}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', m: 0 }}
                  control={(
                    <Checkbox
                      checked={selected.has(move.item.id)}
                      disabled={busy}
                      onChange={(event) => toggle(move.item.id, event.target.checked)}
                      sx={{ pt: 0.5 }}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="body2">
                        {move.summary}
                        {move.adjusted ? ' (that time does not exist that day, so it moves to the next valid time)' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{reason}</Typography>
                    </Box>
                  )}
                />
              </Box>
            );
          })}
        </Stack>

        {cancelled.length > 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {cancelled.length} cancelled {eventWord(cancelled.length)} keep{cancelled.length === 1 ? 's' : ''} the
            old zone — a cancellation is retained history and cannot be edited here.
          </Typography>
        ) : null}
        {unconvertible.length > 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {unconvertible.length} {eventWord(unconvertible.length)} cannot be moved automatically
            ({unconvertible.map((s) => s.item.title || 'Untitled event').join(', ')}) and keep the old zone.
          </Typography>
        ) : null}

        <Alert severity="info" sx={{ mt: 2 }}>
          Nothing is sent to guests from here. Guests see the zone beside each time, so if you move
          an event they can see, you will be asked next whether to let them know — and
          &lsquo;Don&apos;t send&rsquo; is the default.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || (!savesZone && count === 0)}
          onClick={() => onConfirm(chosen)}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
